// Modal tworzenia nowej kalkulacji - pyta o nazwe (wymagana), ilosc (wymagana) i opis (opcjonalny).
// Krok 2 flowu "Dodaj kalkulacje" po wybraniu "Stworz kalkulacje" w ChooseCalculationActionModal.

import React, { type FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, CircularProgress, TextField } from '@mui/material';

import { WiseModal } from 'components/wise';

import styles from 'theme/components/containers/TraderClientOfferProductsContainer/CreateCalculationModal.module.scss';

export interface ICreateCalculationFormValues {
  name: string;
  quantity: number;
  description: string;
}

interface ICreateCalculationModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (values: ICreateCalculationFormValues) => void;
  initialName?: string;
  isSubmitting?: boolean;
}

const CreateCalculationModal: FC<ICreateCalculationModalProps> = ({
  open,
  onClose,
  onSubmit,
  initialName = '',
  isSubmitting = false
}) => {
  const { t } = useTranslation();
  const [name, setName] = useState(initialName);
  const [quantity, setQuantity] = useState<string>('1');
  const [description, setDescription] = useState('');
  const [nameError, setNameError] = useState<string | null>(null);
  const [quantityError, setQuantityError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setName(initialName);
      setQuantity('1');
      setDescription('');
      setNameError(null);
      setQuantityError(null);
    }
  }, [open, initialName]);

  const handleSubmit = () => {
    const trimmedName = name.trim();
    const parsedQuantity = Number(quantity);

    let hasError = false;
    if (!trimmedName) {
      setNameError(t('salesCrm.calculation.create.nameRequired'));
      hasError = true;
    } else {
      setNameError(null);
    }

    if (!quantity.trim() || Number.isNaN(parsedQuantity)) {
      setQuantityError(t('salesCrm.calculation.create.quantityRequired'));
      hasError = true;
    } else if (!Number.isFinite(parsedQuantity) || parsedQuantity <= 0) {
      setQuantityError(t('salesCrm.calculation.create.quantityPositive'));
      hasError = true;
    } else {
      setQuantityError(null);
    }

    if (hasError) return;

    onSubmit({
      name: trimmedName,
      quantity: Math.trunc(parsedQuantity),
      description: description.trim()
    });
  };

  return (
    <WiseModal
      open={open}
      onClose={onClose}
      title={t('salesCrm.calculation.create.title')}
      maxWidth={520}>
      <div className={styles.body}>
        <TextField
          label={t('salesCrm.calculation.create.name')}
          value={name}
          onChange={(e) => setName(e.target.value)}
          error={!!nameError}
          helperText={nameError ?? t('salesCrm.calculation.create.nameHint')}
          fullWidth
          required
          autoFocus
        />

        <TextField
          label={t('salesCrm.calculation.create.quantity')}
          value={quantity}
          onChange={(e) => setQuantity(e.target.value)}
          error={!!quantityError}
          helperText={quantityError ?? t('salesCrm.calculation.create.quantityHint')}
          type="number"
          inputProps={{ min: 1, step: 1 }}
          fullWidth
          required
        />

        <TextField
          label={t('salesCrm.calculation.create.description')}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          helperText={t('salesCrm.calculation.create.descriptionHint')}
          multiline
          minRows={3}
          fullWidth
        />

        <div className={styles.actions}>
          <Button onClick={onClose} variant="text" disabled={isSubmitting}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={isSubmitting}
            sx={{ backgroundColor: '#6b54ff', '&:hover': { backgroundColor: '#5a47e0' } }}>
            {isSubmitting ? (
              <CircularProgress size={18} />
            ) : (
              t('salesCrm.calculation.create.submit')
            )}
          </Button>
        </div>
      </div>
    </WiseModal>
  );
};

export default CreateCalculationModal;
